import React, { useState, useEffect } from "react";
import Modal from "react-bootstrap/Modal";
import { UploadCloud, X, AlertCircle, CheckCircle } from "lucide-react";
import { notifyError, notifyInfo, notifySuccess } from "../../utils/helper";
import { uploadCSVfile } from "../../requests";
import { useDispatch } from "react-redux";
import { startLoader, stopLoader } from "../../_store/reducers/loaderReducer";
import "../../_css/UserManagement.css";

const AddUserModal = ({ show, onHide, onUserCreated }) => {
    const [videoFile, setVideoFile] = useState(null);
    const [uploadResult, setUploadResult] = useState(null); // Store backend response stats
    const dispatch = useDispatch();

    // Reset state when modal closes
    const handleClose = () => {
        setVideoFile(null);
        setUploadResult(null);
        onHide();
    };

    // Reset state when modal opens
    useEffect(() => {
        if (show) {
            setVideoFile(null);
            setUploadResult(null);
        }
    }, [show]);

    const handleUpload = async () => {
        if (!videoFile) {
            notifyInfo("Please select a CSV file");
            return;
        }

        // Check file type
        if (videoFile.type !== 'text/csv' && !videoFile.name.endsWith('.csv')) {
            notifyError("Please select a valid CSV file");
            return;
        }

        const formData = new FormData();
        formData.append("file", videoFile);

        dispatch(startLoader());

        try {
            const data = await uploadCSVfile(formData);

            // Handle the bulk response structure
            if (data.success) {
                // If there were ANY errors, show the report in the modal
                if (data.errorCount > 0) {
                    setUploadResult(data);
                    notifyInfo("Upload completed with some errors.");

                    // If at least one user was added, refresh the table behind the modal
                    if (data.successCount > 0 && onUserCreated) {
                        onUserCreated();
                    }
                }
                // Perfect Scenario: 0 errors
                else {
                    notifySuccess(data.message || "All users added successfully");
                    if (onUserCreated) onUserCreated();
                    handleClose();
                }
            } else {
                // Generic failure (server error before processing rows)
                notifyError(data.message || "Error while uploading CSV file");
            }
        } catch (err) {
            console.error("Upload failed", err);
            const msg = err.response?.data?.message || err.message || "Failed to upload CSV file.";
            notifyError(msg);
        } finally {
            dispatch(stopLoader());
        }
    };

    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            if (file.type !== 'text/csv' && !file.name.endsWith('.csv')) {
                notifyError("Please select a CSV file");
                e.target.value = '';
                return;
            }
            setVideoFile(file);
            setUploadResult(null); // Reset result if picking a new file
        }
    };

    const handleReset = () => {
        setVideoFile(null);
        setUploadResult(null);
    };

    return (
        <Modal
            show={show}
            onHide={handleClose}
            centered
            dialogClassName="modal-38"
        >
            <Modal.Body>
                <div className="add-user-modal-container">
                    <div className="add-user-modal-header">
                        <X onClick={handleClose} className="add-user-modal-close" />
                        <span className="add-user-modal-title">
                            {uploadResult ? "Upload Summary" : "To Add User Upload CSV"}
                        </span>
                    </div>

                    {/* VIEW 1: UPLOAD RESULT REPORT (If upload processed) */}
                    {uploadResult ? (
                        <div className="px-3">
                            <div className="d-flex justify-content-between mb-3 text-center">
                                <div className="p-2 border rounded w-100 me-2" style={{ backgroundColor: '#F0FDF4', borderColor: '#BBF7D0' }}>
                                    <h6 className="text-success fw-bold mb-0">{uploadResult.successCount}</h6>
                                    <small className="text-success">Success</small>
                                </div>
                                <div className="p-2 border rounded w-100 ms-2" style={{ backgroundColor: '#FEF2F2', borderColor: '#FECACA' }}>
                                    <h6 className="text-danger fw-bold mb-0">{uploadResult.errorCount}</h6>
                                    <small className="text-danger">Failed</small>
                                </div>
                            </div>

                            {uploadResult.errorCount > 0 && (
                                <div className="mt-3">
                                    <h6 className="fw-bold mb-2 text-muted" style={{ fontSize: '14px' }}>Error Details:</h6>
                                    <div
                                        className="border rounded p-2 custom-scrollbar"
                                        style={{ maxHeight: '200px', overflowY: 'auto', backgroundColor: '#F9FAFB' }}
                                    >
                                        {uploadResult.errors.map((err, idx) => (
                                            <div key={idx} className="mb-2 pb-2 border-bottom last-border-none" style={{ fontSize: '13px' }}>
                                                <div className="d-flex align-items-start gap-2">
                                                    <AlertCircle size={14} className="text-danger mt-1 flex-shrink-0" />
                                                    <div>
                                                        <span className="fw-bold text-dark">Row {err.row}: </span>
                                                        <span className="text-muted">{err.email || 'Unknown Email'}</span>
                                                        <div className="text-danger mt-1">{err.error}</div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ) : (
                        /* VIEW 2: FILE UPLOAD INPUT (Default) */
                        <div>
                            <div className="add-user-modal-upload-area">
                                <UploadCloud size={48} color="#000000" />

                                {!videoFile ? (
                                    <>
                                        <label
                                            htmlFor={`file-upload-0`}
                                            className="add-user-modal-upload-label"
                                        >
                                            Select a CSV file to Upload
                                        </label>
                                        <p className="add-user-modal-upload-subtext">
                                            Or drag and drop
                                        </p>
                                    </>
                                ) : (
                                    <>
                                        <p className="add-user-modal-filename">
                                            {videoFile.name}
                                        </p>
                                        <label
                                            htmlFor={`file-upload-0`}
                                            className="add-user-modal-change-file"
                                        >
                                            Change file
                                        </label>
                                    </>
                                )}

                                <input
                                    id={`file-upload-0`}
                                    type="file"
                                    accept=".csv"
                                    className="add-user-modal-file-input"
                                    onChange={handleFileChange}
                                />
                            </div>
                        </div>
                    )}

                    {/* BUTTONS */}
                    <div className="add-user-modal-buttons">
                        {uploadResult ? (
                            // Buttons shown after upload
                            <>
                                <button
                                    className="add-user-modal-back-button"
                                    onClick={handleClose}
                                >
                                    Close
                                </button>
                                <button
                                    className="add-user-modal-next-button"
                                    onClick={handleReset}
                                >
                                    Upload Another
                                </button>
                            </>
                        ) : (
                            // Buttons shown before upload
                            <>
                                <button
                                    className="add-user-modal-back-button"
                                    onClick={handleClose}
                                >
                                    Back
                                </button>
                                <button
                                    className="add-user-modal-next-button"
                                    onClick={handleUpload}
                                >
                                    Add User
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </Modal.Body>
        </Modal>
    );
};

export default AddUserModal;